/* ==========================================================================
   SHOESLI V5 — brand marquee

   Continuous auto-scroll that the shopper can grab. Built on a real scroll
   container rather than a transform, so:
     - native inertia, wheel and trackpad gestures work,
     - RTL is handled by the browser instead of by sign-juggling,
     - keyboard users get arrow keys and a focusable viewport for free.

   Auto-scroll pauses on hover, focus and pointer-down, and resumes after a
   short idle so a shopper reading a logo is never dragged away from it.
   ========================================================================== */
(function () {
  'use strict';

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  class V5Marquee extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('.v5-marquee__viewport');
      this.track = this.querySelector('.v5-marquee__track');
      if (!this.viewport || !this.track) return;

      this.items = Array.from(this.track.children);
      if (!this.items.length) return;

      this.autoplay = this.dataset.autoplay === 'true';
      this.speed = Number(this.dataset.speed || 30);
      this.paused = false;
      this.dragging = false;
      this.resumeTimer = null;

      this.cloneForLoop();
      this.bindPointer();
      this.bindPause();

      // Start in the middle copy so the shopper can scroll back to brands that
      // have already gone past, in either direction. Done synchronously —
      // cloneForLoop has already forced layout by reading scrollWidth, and a
      // deferred rAF is not guaranteed to run before the rail is looked at.
      this.pos = this.loopWidth;
      this.setScroll(this.loopWidth);

      // Running is the default. IntersectionObserver is only an optimisation
      // that parks the loop while the rail is off screen — if it never
      // delivers a callback, the marquee still moves rather than sitting dead.
      this.visible = true;
      this.maybeStart();
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(
          ([entry]) => {
            this.visible = entry.isIntersecting;
            if (this.visible) this.maybeStart();
            else this.stop();
          },
          { rootMargin: '200px 0px' }
        ).observe(this);
      }

      reduced.addEventListener('change', () => {
        if (reduced.matches) this.stop();
        else this.maybeStart();
      });
    }

    /* Three copies: [prev][real][next] gives room to drift either way. */
    cloneForLoop() {
      const frag = document.createDocumentFragment();
      const back = document.createDocumentFragment();
      this.items.forEach((el) => {
        const a = el.cloneNode(true);
        const b = el.cloneNode(true);
        [a, b].forEach((c) => {
          c.setAttribute('aria-hidden', 'true');
          c.querySelectorAll('a').forEach((link) => link.setAttribute('tabindex', '-1'));
        });
        frag.appendChild(a);
        back.appendChild(b);
      });
      this.track.insertBefore(back, this.track.firstChild);
      this.track.appendChild(frag);
      this.loopWidth = this.track.scrollWidth / 3;
    }

    /* Work in a direction-agnostic "distance from start" so RTL, where
       scrollLeft runs negative in some engines, needs no special casing. */
    getScroll() { return Math.abs(this.viewport.scrollLeft); }
    setScroll(v) {
      const sign = this.viewport.scrollLeft < 0 || getComputedStyle(this.viewport).direction === 'rtl' ? -1 : 1;
      this.viewport.scrollLeft = sign * v;
    }

    normalise() {
      const x = this.getScroll();
      if (x < this.loopWidth * 0.5) this.setScroll(x + this.loopWidth);
      else if (x > this.loopWidth * 1.5) this.setScroll(x - this.loopWidth);
    }

    maybeStart() {
      if (!this.autoplay || reduced.matches || !this.visible) return;
      this.start();
    }

    start() {
      if (this.raf || this.timer) return;
      // px per frame from the configured full-loop duration
      const perFrame = this.loopWidth / (this.speed * 60);
      // Track the position as a float. Reading scrollLeft back each frame and
      // adding a sub-pixel delta loses it to rounding, and the rail never moves.
      if (typeof this.pos !== 'number') this.pos = this.getScroll();

      const advance = (dt) => {
        if (this.paused || this.dragging) return;
        this.pos += perFrame * dt;
        if (this.pos > this.loopWidth * 1.5) this.pos -= this.loopWidth;
        else if (this.pos < this.loopWidth * 0.5) this.pos += this.loopWidth;
        this.setScroll(this.pos);
      };

      let last = performance.now();
      let ticked = false;
      const step = (now) => {
        ticked = true;
        const dt = Math.min((now - last) / 16.67, 3);
        last = now;
        advance(dt);
        this.raf = requestAnimationFrame(step);
      };
      this.raf = requestAnimationFrame(step);

      // Some embedded webviews never deliver requestAnimationFrame. If no frame
      // has arrived shortly after starting, drive the rail from a timer instead
      // so it degrades to "slightly less smooth" rather than "broken".
      clearTimeout(this.rafProbe);
      this.rafProbe = setTimeout(() => {
        if (ticked) return;
        if (this.raf) { cancelAnimationFrame(this.raf); this.raf = null; }
        let t = performance.now();
        this.timer = setInterval(() => {
          const now = performance.now();
          advance(Math.min((now - t) / 16.67, 4));
          t = now;
        }, 33);
      }, 400);
    }

    /* Any scroll the shopper causes becomes the new source of truth. */
    syncPos() { this.pos = this.getScroll(); }

    stop() {
      clearTimeout(this.rafProbe);
      if (this.raf) { cancelAnimationFrame(this.raf); this.raf = null; }
      if (this.timer) { clearInterval(this.timer); this.timer = null; }
    }

    pause() {
      this.paused = true;
      clearTimeout(this.resumeTimer);
    }

    resumeSoon(delay = 1800) {
      clearTimeout(this.resumeTimer);
      this.resumeTimer = setTimeout(() => {
        this.paused = false;
        this.maybeStart();
      }, delay);
    }

    bindPause() {
      const vp = this.viewport;
      vp.addEventListener('mouseenter', () => this.pause());
      vp.addEventListener('mouseleave', () => this.resumeSoon(600));
      vp.addEventListener('focusin', () => this.pause());
      vp.addEventListener('focusout', () => this.resumeSoon());
      // A shopper flicking the rail by touch or wheel owns it for a moment.
      vp.addEventListener('scroll', () => {
        if (this.paused || this.dragging) { this.normalise(); this.syncPos(); }
      }, { passive: true });
      vp.addEventListener('wheel', () => { this.pause(); this.resumeSoon(); }, { passive: true });
      vp.addEventListener('touchstart', () => this.pause(), { passive: true });
      vp.addEventListener('touchend', () => this.resumeSoon());
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.pause();
        else this.resumeSoon(400);
      });
    }

    /* Desktop drag. Touch is left to native scrolling, which already has the
       right inertia and rubber-banding. */
    bindPointer() {
      const vp = this.viewport;
      let startX = 0, startScroll = 0, moved = 0, pid = null;

      vp.addEventListener('pointerdown', (e) => {
        if (e.pointerType === 'touch') return;
        pid = e.pointerId;
        this.dragging = true;
        this.pause();
        moved = 0;
        startX = e.clientX;
        startScroll = this.getScroll();
        vp.setPointerCapture(pid);
        vp.classList.add('is-dragging');
      });

      vp.addEventListener('pointermove', (e) => {
        if (!this.dragging || e.pointerId !== pid) return;
        const dx = e.clientX - startX;
        moved = Math.max(moved, Math.abs(dx));
        // Dragging inline-start should reveal what is inline-end of it, in
        // both writing directions.
        const dir = getComputedStyle(vp).direction === 'rtl' ? 1 : -1;
        this.setScroll(startScroll + dir * dx);
        this.normalise();
        this.syncPos();
      });

      const end = (e) => {
        if (!this.dragging || (pid !== null && e.pointerId !== pid)) return;
        this.dragging = false;
        vp.classList.remove('is-dragging');
        try { vp.releasePointerCapture(pid); } catch (err) {}
        pid = null;
        this.resumeSoon();
      };
      vp.addEventListener('pointerup', end);
      vp.addEventListener('pointercancel', end);

      // A drag must not fire the link underneath it.
      vp.addEventListener('click', (e) => { if (moved > 6) { e.preventDefault(); e.stopPropagation(); } }, true);

      vp.addEventListener('keydown', (e) => {
        const stepBy = vp.clientWidth * 0.6;
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
          const rtl = getComputedStyle(vp).direction === 'rtl';
          const forward = rtl ? e.key === 'ArrowLeft' : e.key === 'ArrowRight';
          e.preventDefault();
          this.pause();
          this.setScroll(this.getScroll() + (forward ? stepBy : -stepBy));
          this.normalise();
          this.syncPos();
          this.resumeSoon();
        }
      });
    }
  }

  if (!customElements.get('v5-marquee')) customElements.define('v5-marquee', V5Marquee);
})();
